// src/store/bookingStore.js
import { defineStore } from 'pinia'

export const useBookingStore = defineStore('bookingStore', {
    state: () => ({
        // ── 篩選資料 ──────────────────────────────
        regions:    [],
        categories: [],
        isLoadedRegions:    false,
        isLoadedCategories: false,

        // ── 店家列表 ──────────────────────────────
        stores:         [],
        storesLoading:  false,
        storesError:    null,

        // ── 店家詳情（含 services + hours + categories）─────────
        currentStore:      null,
        currentServices:   [],
        currentHours:      [],
        currentCategories: [],
        storeLoading:    false,
        storeError:      null,

        // ── 可用時段 ──────────────────────────────
        slots:        [],
        totalSlots:   0,   // 該日實際存在的時段數（0 = 休假，>0 但全 available=0 = 額滿）
        slotsLoading: false,
        slotsError:   null,

        // ── 預約成功後的狀態（跨頁用）────────────
        reservationCode:  null,   // POST 成功後由 BookingReserve 寫入
        isMember:         false,  // 同上
        reservationEmail: null,   // 預約時填寫的 Email，供成功頁顯示

        // ── 訪客查詢結果 ──────────────────────────
        queriedReservation:  null,
        queriedReservations: [],   // by-email 查詢的多筆結果
        queryLoading:        false,
        queryError:          null,

        // ── 會員預約列表 ──────────────────────────
        memberReservations:        [],
        memberReservationsLoading: false,
        memberReservationsError:   null,
        memberUnauthorized:        false,  // true = API 回 401，需導向登入
    }),

    actions: {
        // ── 地區（快取，只撈一次）────────────────
        async fetchRegions() {
            if (this.isLoadedRegions) return
            try {
                const res  = await fetch('/api/booking/regions')
                const json = await res.json()
                if (json.success) {
                    this.regions          = json.regions
                    this.isLoadedRegions  = true
                } else {
                    console.warn('fetchRegions failed:', json.message)
                }
            } catch (e) {
                console.error('fetchRegions error:', e)
            }
        },

        // ── 分類（快取，只撈一次）────────────────
        async fetchCategories() {
            if (this.isLoadedCategories) return
            try {
                const res  = await fetch('/api/booking/categories')
                const json = await res.json()
                if (json.success) {
                    this.categories          = json.categories
                    this.isLoadedCategories  = true
                } else {
                    console.warn('fetchCategories failed:', json.message)
                }
            } catch (e) {
                console.error('fetchCategories error:', e)
            }
        },

        // ── 店家列表（每次篩選條件不同，不快取）──
        async fetchStores({ regionId = null, categoryId = null } = {}) {
            this.storesLoading = true
            this.storesError   = null
            try {
                const params = new URLSearchParams()
                if (regionId)   params.set('region_id',   regionId)
                if (categoryId) params.set('category_id', categoryId)
                const res  = await fetch(`/api/booking/stores?${params}`)
                const json = await res.json()
                if (json.success) {
                    this.stores = json.stores
                } else {
                    this.storesError = json.message || '載入失敗'
                }
            } catch (e) {
                console.error('fetchStores error:', e)
                this.storesError = '網路錯誤，請稍後再試'
            } finally {
                this.storesLoading = false
            }
        },

        // ── 店家詳情 ──────────────────────────────
        async fetchStore(storeId) {
            this.storeLoading  = true
            this.storeError    = null
            this.currentStore      = null
            this.currentServices   = []
            this.currentHours      = []
            this.currentCategories = []
            try {
                const res  = await fetch(`/api/booking/stores/${storeId}`)
                const json = await res.json()
                if (json.success) {
                    this.currentStore      = json.store
                    this.currentServices   = json.services
                    this.currentHours      = json.hours
                    this.currentCategories = json.categories || []
                } else {
                    this.storeError = json.message || '店家不存在'
                }
            } catch (e) {
                console.error('fetchStore error:', e)
                this.storeError = '網路錯誤，請稍後再試'
            } finally {
                this.storeLoading = false
            }
        },

        // ── 可用時段 ──────────────────────────────
        // date: 'YYYY-MM-DD'，duration: 服務時長（分鐘，選填）
        // 傳入 duration 時後端只回傳「能連續覆蓋整段服務時間」的起始時段
        async fetchSlots(storeId, date, duration = null, teacher_name = null) {
            this.slotsLoading = true
            this.slotsError   = null
            this.slots        = []
            this.totalSlots   = 0
            try {
                const params = new URLSearchParams({ date })
                if (duration) params.set('duration', duration)
                if (teacher_name) params.set('teacher_name', teacher_name)
                const res  = await fetch(`/api/booking/stores/${storeId}/slots?${params}`)
                const json = await res.json()
                if (json.success) {
                    this.slots      = json.slots
                    this.totalSlots = json.totalSlots ?? json.slots.length
                } else {
                    this.slotsError = json.message || '無法載入時段'
                }
            } catch (e) {
                console.error('fetchSlots error:', e)
                this.slotsError = '網路錯誤，請稍後再試'
            } finally {
                this.slotsLoading = false
            }
        },

        // ── 建立預約 ──────────────────────────────
        // 成功後將 code / isMember 存入 store，BookingSuccess 直接取用。
        // 回傳 { success, reservation_code, is_member, message } 供元件判斷。
        async createReservation(payload) {
            try {
                const res  = await fetch('/api/booking/reservations', {
                    method:      'POST',
                    headers:     { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body:        JSON.stringify(payload),
                })
                const json = await res.json()
                if (json.success) {
                    this.reservationCode  = json.reservation_code
                    this.isMember         = json.is_member
                    this.reservationEmail = payload.customer_email || null
                }
                return json   // 讓元件自行處理錯誤訊息顯示
            } catch (e) {
                console.error('createReservation error:', e)
                return { success: false, message: '網路錯誤，請稍後再試' }
            }
        },

        // ── 訪客以 Email 查詢預約（多筆）─────────
        async queryByEmail(email, phoneLast4) {
            this.queryLoading        = true
            this.queryError          = null
            this.queriedReservations = []
            try {
                const params = new URLSearchParams({ email, phone_last4: phoneLast4 })
                const res  = await fetch(`/api/booking/reservations/by-email?${params}`)
                const json = await res.json()
                if (json.success) {
                    this.queriedReservations = json.reservations
                } else {
                    this.queryError = json.message || '查詢失敗'
                }
                return json
            } catch (e) {
                console.error('queryByEmail error:', e)
                this.queryError = '網路錯誤，請稍後再試'
                return { success: false, message: this.queryError }
            } finally {
                this.queryLoading = false
            }
        },

        // ── 訪客查詢預約（舊版，以代碼查詢）────
        // code: reservation_code，phoneLast4: 手機末 4 碼
        async queryReservation(code, phoneLast4) {
            this.queryLoading      = true
            this.queryError        = null
            this.queriedReservation = null
            try {
                const res  = await fetch(
                    `/api/booking/reservations/${encodeURIComponent(code)}?phone_last4=${encodeURIComponent(phoneLast4)}`
                )
                const json = await res.json()
                if (json.success) {
                    this.queriedReservation = json.reservation
                } else {
                    this.queryError = json.message || '查詢失敗'
                }
                return json
            } catch (e) {
                console.error('queryReservation error:', e)
                this.queryError = '網路錯誤，請稍後再試'
                return { success: false, message: this.queryError }
            } finally {
                this.queryLoading = false
            }
        },

        // ── 會員預約列表 ──────────────────────────
        async fetchMemberReservations() {
            this.memberReservationsLoading = true
            this.memberReservationsError   = null
            this.memberUnauthorized        = false
            try {
                const res = await fetch('/api/booking/member/reservations', {
                    credentials: 'include',
                })
                // 依 HTTP status code 判斷 401，不靠錯誤訊息字串
                if (res.status === 401) {
                    this.memberUnauthorized = true
                    return { success: false, unauthorized: true }
                }
                const json = await res.json()
                if (json.success) {
                    this.memberReservations = json.reservations
                } else {
                    this.memberReservationsError = json.message || '載入失敗'
                }
                return json
            } catch (e) {
                console.error('fetchMemberReservations error:', e)
                this.memberReservationsError = '網路錯誤，請稍後再試'
                return { success: false, message: this.memberReservationsError }
            } finally {
                this.memberReservationsLoading = false
            }
        },

        // ── 訪客線上取消預約（不需登入）──────────
        async cancelGuestReservation(code, email, phone_last4) {
            try {
                const res = await fetch(
                    `/api/booking/reservations/${encodeURIComponent(code)}/cancel`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email, phone_last4 }),
                    }
                )
                return res.json()
            } catch (e) {
                console.error('cancelGuestReservation error:', e)
                return { success: false, message: '網路錯誤，請稍後再試' }
            }
        },

        // ── 會員取消預約 ──────────────────────────
        // 成功後從列表中更新該筆狀態，不重新撈取整份列表。
        async cancelReservation(code) {
            try {
                const res  = await fetch(
                    `/api/booking/member/reservations/${encodeURIComponent(code)}/cancel`,
                    {
                        method:      'PUT',
                        credentials: 'include',
                    }
                )
                const json = await res.json()
                if (json.success) {
                    const target = this.memberReservations.find(r => r.reservation_code === code)
                    if (target) target.status = 'cancelled'
                }
                return json
            } catch (e) {
                console.error('cancelReservation error:', e)
                return { success: false, message: '網路錯誤，請稍後再試' }
            }
        },

        // ── 清除當前店家資料（離開頁面時用）─────
        clearCurrentStore() {
            this.currentStore      = null
            this.currentServices   = []
            this.currentHours      = []
            this.currentCategories = []
            this.slots             = []
            this.slotsError        = null
        },
    },
})
